import { compareAsNumbers } from 'comparators';

import { ACCOUNT_CODES, ACCOUNT_CATEGORY_IDS } from './constants';
import {
  compareAllDataEntries,
  dataEntryComparators,
  ledgerAccountComparators,
} from './comparators';
import ledgerAccountCategoryTypes from './ledgerAccountCategoryTypes';
import ledgerAccountOrderingTypes from './ledgerAccountOrderingTypes';
import ledgerCategoryOrderingTypes from './ledgerCategoryOrderingTypes';
import type {
  AggregateBalanceSummaries,
  DataEntry,
  JournalEntry,
  LedgerAccount,
  LedgerAccountCategory,
  LedgerAccountWithJournalEntries,
  Contractor,
} from './types';
import type { Task as ConfirmCategoryTask } from './components/task-operations/types';

// Ledger accounts with these codes are sorted differently in the general ledger
const moneyInTransitLedgerAccountCodes = ['1998', '1999'];

// Maps ledger account category ids to the position that they should appear in the GL
const ledgerAccountCategoryOrderingMap = {
  // Assets
  '1': 0,
  // Liabilities
  '3': 1,
  // Equity
  '5': 2,
  // Revenues
  '6': 3,
  // Cost of Sales
  '8': 4,
  // Operating Expenses
  '7': 5,
};

const ledgerAccountCategoryTypeMap = {
  // Assets
  '1': ledgerAccountCategoryTypes.BALANCE_SHEET,
  // Liabilities
  '3': ledgerAccountCategoryTypes.BALANCE_SHEET,
  // Equity
  '5': ledgerAccountCategoryTypes.BALANCE_SHEET,
  // Revenues
  '6': ledgerAccountCategoryTypes.INCOME_STATEMENT,
  // Cost of Sales
  '8': ledgerAccountCategoryTypes.INCOME_STATEMENT,
  // Operating Expenses
  '7': ledgerAccountCategoryTypes.INCOME_STATEMENT,
};

// A transfer ID is used to associate the two journal entries that were matched with the
// "MITO matching" feature. For money in transit, a transfer is represented by two journal entries
// that are linked to each other via the "relatedJEId" field to indicate they are part of the same
// transfer. A transfer ID is a unique identifier that is the same for both journal entries in a
// transfer.
export const createTransferId = (journalEntryId: string, relatedJEId: string) => {
  if (!journalEntryId || !relatedJEId) {
    return '';
  }
  if (journalEntryId > relatedJEId) {
    return relatedJEId + journalEntryId;
  }
  return journalEntryId + relatedJEId;
};

export const compileLedgerAccountCategoryObjects = (ledgerAccountCategoryOrders: Array<any>) => {
  const ledgerAccountCategoriesMap: Map<string, LedgerAccountCategory> = new Map();
  ledgerAccountCategoryOrders
    .filter(order => order.ledgerAccountCategory.id !== '9')
    .forEach(({ ledgerAccountCategory: { id, name }, ledgerCategoryOrderingType }) => {
      ledgerAccountCategoriesMap.set(id, {
        id,
        ledgerAccounts: [],
        ledgerAccountCategoryType: ledgerAccountCategoryTypeMap[id],
        ledgerCategoryOrderingType,
        name,
        order: ledgerAccountCategoryOrderingMap[id],
      });
    });
  return ledgerAccountCategoriesMap;
};

const compileDataAndJournalEntryObjects = (
  journalEntries: Array<JournalEntry>,
  ledgerAccountsMap: Map<string, LedgerAccount>,
  categorizationInconsistencies,
  contractors: Array<Contractor>,
  dataEntryToTaskMap: Map<
    string,
    { id: string; status: string; createdBy?: string; createdOn?: string; resolvedOn?: string }
  > = new Map(),
) => {
  const inconsistentDataEntryIdSet = categorizationInconsistencies.reduce(
    (allInconsistentDataEntryIds, { dataEntryIds }) => {
      // Each categorization inconsistency has a list of data entry IDs that should be marked as
      // being inconsistently categorized, so put them all in one set to check against
      dataEntryIds.forEach(id => allInconsistentDataEntryIds.add(id));
      return allInconsistentDataEntryIds;
    },
    new Set(),
  );
  const journalEntriesMap: Map<string, JournalEntry> = new Map();
  const dataEntriesMap: Map<string, DataEntry> = new Map();
  journalEntries.forEach(originalJournalEntry => {
    const transferId = createTransferId(
      originalJournalEntry.id,
      originalJournalEntry.relatedJE && originalJournalEntry.relatedJE.id,
    );
    const clientCatLedger =
      originalJournalEntry.clientCatLedger &&
      ledgerAccountsMap.get(originalJournalEntry.clientCatLedger.id);
    const contractor = contractors?.length
      ? contractors.find(contractor => contractor.id === originalJournalEntry.contractorId)
      : null;
    const journalEntry = {
      ...originalJournalEntry,
      clientCatLedger,
      dataEntries: [] as Array<DataEntry>,
      transferId,
      contractorName: contractor?.name,
    };
    originalJournalEntry.dataEntries.forEach(originalDataEntry => {
      const hasPotentialQualityIssues = inconsistentDataEntryIdSet.has(originalDataEntry.id);
      const ledgerAccount = ledgerAccountsMap.get(originalDataEntry.ledgerAccount.id);
      const taskInfo = dataEntryToTaskMap.get(originalDataEntry.id);
      const dataEntry = {
        ...originalDataEntry,
        hasPotentialQualityIssues,
        journalEntry,
        ledgerAccount,
        ...(taskInfo && { task: taskInfo }),
      };
      journalEntry.dataEntries.push(dataEntry);
      if (ledgerAccount) {
        ledgerAccount.dataEntries.push(dataEntry);
      }
      dataEntriesMap.set(dataEntry.id, dataEntry);
    });
    journalEntriesMap.set(originalJournalEntry.id, journalEntry);
  });
  return {
    dataEntriesMap,
    journalEntriesMap,
  };
};

const sortListProperties = ledgerAccountCategoriesMap => {
  ledgerAccountCategoriesMap.forEach(({ ledgerAccounts, ledgerCategoryOrderingType }) => {
    const ledgerAccountComparator =
      ledgerAccountComparators[ledgerCategoryOrderingType || ledgerCategoryOrderingTypes.CUSTOM];
    ledgerAccounts.sort(ledgerAccountComparator);
    ledgerAccounts.forEach(({ code, dataEntries }) => {
      const ledgerAccountOrderingType = moneyInTransitLedgerAccountCodes.includes(code)
        ? ledgerAccountOrderingTypes.MONEY_IN_TRANSIT
        : ledgerAccountOrderingTypes.DEFAULT;
      const dataEntryComparator = dataEntryComparators[ledgerAccountOrderingType];
      dataEntries.sort(dataEntryComparator);
    });
  });
};

const getSortedLedgerAccountCategories = (
  ledgerAccountCategoriesMap: Map<string, LedgerAccountCategory>,
): Array<LedgerAccountCategory> =>
  Array.from(ledgerAccountCategoriesMap.values()).sort((categoryA, categoryB) =>
    compareAsNumbers(categoryA.order, categoryB.order),
  );

const getSortedDataEntries = (dataEntriesMap: Map<string, DataEntry>): Array<DataEntry> =>
  Array.from(dataEntriesMap.values()).sort(compareAllDataEntries);

/**
 * Compute potential transaction matches during data compilation
 * This was moved from makeGeneralLedgerListItemData to avoid expensive computation on every render
 */
const computePotentialMatches = (dataEntries: Array<DataEntry>) => {
  const creditTransactions = new Map<number, DataEntry[]>();
  const debitTransactions = new Map<number, DataEntry[]>();

  dataEntries.forEach(entry => {
    const { ledgerAccount } = entry;
    const isFunctionalAccount = ledgerAccount?.role;
    const isMoneyInTransit = ledgerAccount?.code === ACCOUNT_CODES.MONEY_IN_TRANSIT;
    const isPersonalBusinessAccount =
      ledgerAccount?.category?.id === ACCOUNT_CATEGORY_IDS.PERSONAL_BUSINESS;

    // Exclude entries belonging to Functional, Personal accounts or from MIT account
    if (isFunctionalAccount || isPersonalBusinessAccount || isMoneyInTransit) {
      return;
    }

    // We want to exclude adjustment entries from the potential match list
    if (entry.journalEntry.vendorName.toLowerCase().includes('adjustment |')) {
      return;
    }

    const amount = entry.amountInCents;
    const targetMap = amount < 0 ? creditTransactions : debitTransactions;

    if (targetMap.has(amount)) {
      targetMap.get(amount)?.push(entry);
    } else {
      targetMap.set(amount, [entry]);
    }
  });

  return { creditTransactions, debitTransactions };
};

export const compileLedgerAccountObjectsNew = (
  aggregateBalanceSummaries: Array<any>,
  ledgerAccountCategoriesMap: Map<string, LedgerAccountCategory>,
): Map<string, LedgerAccountWithJournalEntries> => {
  const ledgerAccountsMap = new Map<string, any>();
  aggregateBalanceSummaries.forEach(
    ({ closingBalanceCents, inPeriodBalanceCents, openingBalanceCents, ledgerAccount }) => {
      const {
        id,
        category,
        code,
        details,
        internalNotes,
        name,
        role,
        journalEntries,
        financialInstitutionProduct,
        accountLastFourDigits,
      } = ledgerAccount;
      const ledgerAccountCategory = ledgerAccountCategoriesMap.get(category.id);
      const ledger = {
        id,
        category: ledgerAccountCategory,
        code,
        dataEntries: [],
        journalEntries,
        details,
        internalNotes,
        name,
        role,
        financialInstitutionProduct: financialInstitutionProduct
          ? {
              ...financialInstitutionProduct,
              financialInstitution: financialInstitutionProduct.financialInstitution || null,
              financialInstitutionProductType:
                financialInstitutionProduct.financialInstitutionProductType || null,
            }
          : null,
        accountLastFourDigits: accountLastFourDigits || null,
        closingBalanceInCents: closingBalanceCents,
        openingBalanceInCents: openingBalanceCents,
        sumTotalInCents: inPeriodBalanceCents,
      };
      if (ledgerAccountCategory) {
        ledgerAccountCategory.ledgerAccounts.push(ledger);
      }
      ledgerAccountsMap.set(id, ledger);
    },
  );
  return ledgerAccountsMap;
};

export const newCompileQueryData = (
  addOns: Array<any>,
  aggregateBalanceSummaries: AggregateBalanceSummaries,
  journalEntries: Array<JournalEntry>,
  categorizationInconsistencies: Array<any>,
  ledgerAccountCategoryOrders: Array<any>,
  contractors: Array<Contractor>,
  ledgerAccounts: Array<any>,
  confirmCategoryTasks: Array<ConfirmCategoryTask> = [],
): {
  ledgerAccountCategories: Array<LedgerAccountCategory>;
  dataEntries: Array<DataEntry>;
  potentialMatches: {
    creditTransactions: Map<number, DataEntry[]>;
    debitTransactions: Map<number, DataEntry[]>;
  };
  maps: {
    dataEntriesMap: Map<string, DataEntry>;
    journalEntriesMap: Map<string, JournalEntry>;
    ledgerAccountCategoriesMap: Map<string, LedgerAccountCategory>;
    ledgerAccountsMap: Map<string, LedgerAccountWithJournalEntries>;
  };
  moneyInTransitLedgerId?: string;
  confirmCategoryTasks: Array<ConfirmCategoryTask>;
} => {
  const marker1 = 'startInternal';
  const marker2 = 'compileLedgerAccountCategoryObjects';
  const marker3 = 'compileLedgerAccountObjectsNew';
  const marker4 = 'compileDataAndJournalEntryObjects';
  const marker5 = 'sortAllProperties';
  performance.mark(marker1);
  const ledgerAccountCategoriesMap = compileLedgerAccountCategoryObjects(
    ledgerAccountCategoryOrders,
  );
  performance.mark(marker2);
  const ledgerAccountsMap = compileLedgerAccountObjectsNew(
    aggregateBalanceSummaries,
    ledgerAccountCategoriesMap,
  );
  // Used for filtering tasks
  const dataEntryToLedgerAccountIdMap = new Map<string, string>();
  journalEntries.forEach(journalEntry => {
    journalEntry.dataEntries.forEach(dataEntry => {
      if (dataEntry.ledgerAccount?.id) {
        dataEntryToLedgerAccountIdMap.set(dataEntry.id, dataEntry.ledgerAccount.id);
      }
    });
  });

  const dataEntryToTaskMap = new Map<
    string,
    {
      id: string;
      status: string;
      createdOn?: string;
      createdBy?: string;
      resolvedOn?: string;
    }
  >();

  confirmCategoryTasks.forEach((task: ConfirmCategoryTask) => {
    const dataEntryId = task?.job?.parameters?.dataEntry?.id;
    const taskLedgerAccountId = task?.job?.triggerContext?.ledgerAccount?.id;
    const dataEntryLedgerAccountId = dataEntryId
      ? dataEntryToLedgerAccountIdMap.get(dataEntryId)
      : null;
    const ledgersMatch = taskLedgerAccountId === dataEntryLedgerAccountId;
    const isRelevantTask =
      task.status === 'PENDING_WORK' ||
      (task.status === 'RESOLVED' && task.resolution === 'CATEGORY_CONFIRMED');

    if (dataEntryId && task.id && task.status && ledgersMatch && isRelevantTask) {
      dataEntryToTaskMap.set(dataEntryId, {
        id: task.id,
        status: task.status,
        createdBy: task.job?.createdBy?.fullName,
        createdOn: task.createdOn,
        resolvedOn: task.resolvedOn,
      });
    }
  });

  performance.mark(marker3);
  const { dataEntriesMap, journalEntriesMap } = compileDataAndJournalEntryObjects(
    journalEntries,
    ledgerAccountsMap,
    categorizationInconsistencies,
    contractors,
    dataEntryToTaskMap,
  );
  performance.mark(marker4);
  sortListProperties(ledgerAccountCategoriesMap);
  const ledgerAccountCategories = getSortedLedgerAccountCategories(ledgerAccountCategoriesMap);
  const dataEntries = getSortedDataEntries(dataEntriesMap);

  // Compute potential matches once during data compilation
  const potentialMatches = computePotentialMatches(dataEntries);

  performance.mark(marker5);

  performance.measure(`${marker1} to ${marker2}`, marker1, marker2);
  performance.measure(`${marker2} to ${marker3}`, marker2, marker3);
  performance.measure(`${marker3} to ${marker4}`, marker3, marker4);
  performance.measure(`${marker4} to ${marker5}`, marker4, marker5);

  return {
    dataEntries,
    ledgerAccountCategories,
    potentialMatches,
    maps: {
      dataEntriesMap,
      journalEntriesMap,
      ledgerAccountCategoriesMap,
      ledgerAccountsMap,
    },
    moneyInTransitLedgerId: ledgerAccounts?.find(ledgerAccount => ledgerAccount.code === '1999')
      ?.id,
    confirmCategoryTasks,
  };
};

export default newCompileQueryData;
